import {
  ApiError,
  GameTip,
  LaunchStreamOpts,
  StreamMeta,
  cancelStream,
  getGame,
  getGameTips,
  launchStream,
  pollStream,
} from "@blacknut/javascript-sdk/dist";
import { logD, logE, logW } from "@blacknut/logging/dist";
import { datadogLogs } from "@datadog/browser-logs";
import { useCallback, useEffect, useRef, useState } from "react";
import { WebRTCError, WebRTCErrorCode } from "../../model";
import eventEmitter from "../../events/EventEmitter";
import { useAppContext } from "../../utils/ContextProvider";
import { usePlayerContext } from "../../utils/PlayerContext";
import { EVENT_EMITTER_EVENT_STOP, PlayerStatus } from "../../utils/PlayerUtils";
import { LOGGING_TAG } from "../../utils/logging";
import useExitConfirmation from "./useExitConfirmation";
import useBackConfirmation from "./useBackConfirmation";
import { NativeModule } from "@blacknut/playerwebrtc";
import { forkJoin } from "rxjs";

const BACK_VALID_STATUSES = [PlayerStatus.INIT];
const useGameLaunching = ({
  gameId,
  onGameLoaded,
  onError,
  gameParameters,
  onCancelLaunch,
  confirmExit,
}: {
  gameId: string;
  onGameLoaded?: () => void;
  onError: (e: WebRTCError) => void;
  gameParameters?: string[];
  onCancelLaunch?: () => void;
  confirmExit: boolean;
}) => {
  const [gameLoading, setGameLoading] = useState(false);
  const { game, setGame, setStream, setStreamUserConf } = useAppContext();
  const { playerStatus, getKeyboardLayoutMap } = usePlayerContext();
  const [progress, setProgress] = useState(0);
  const streamId = useRef<string>();
  const cancelPolling = useRef<{ cancel: () => void }>();
  const [tips, setTips] = useState<GameTip[]>();
  const [streamMeta, setStreamMeta] = useState<StreamMeta>();
  const statusRef = useRef(playerStatus);
  useEffect(() => {
    statusRef.current = playerStatus;
  }, [playerStatus]);

  useEffect(() => {
    if (!game) {
      setGameLoading(true);

      forkJoin({
        game: getGame(gameId),
        tips: getGameTips(gameId),
      }).subscribe({
        next: (res) => {
          setTips(
            (res.tips.game_launch || []).filter(
              (tip) =>
                !tip.streamProtocols || tip.streamProtocols.indexOf("webrtc") !== -1,
            ),
          );
          setGame(res.game);
          setGameLoading(false);
          onGameLoaded?.();
          NativeModule.module?.onGameLoaded?.();
        },
        error: (err: ApiError) => {
          setGameLoading(false);
          onError({
            code: err.code || WebRTCErrorCode.LAUNCH_ERROR,
            message: err.toString() || "Unknown error",
          });
        },
      });
    }
  }, [game, gameId, onError, onGameLoaded]);

  const canceled = useRef(false);

  const _cancelStream = useCallback(() => {
    if (streamId.current) {
      // will trigger the cancel on FB
      cancelPolling.current?.cancel();
      onCancelLaunch?.();
    } else {
      // https://blacknut.atlassian.net/browse/CA-3731
      // race condition when calling back while /start has not yet returned
      canceled.current = true;
    }
  }, [onCancelLaunch]);

  useExitConfirmation({
    confirmExit,
    validStatus: BACK_VALID_STATUSES,
    onConfirmed: _cancelStream,
  });

  useBackConfirmation({
    validStatus: BACK_VALID_STATUSES,
    onConfirmed: _cancelStream,
    simple: true,
  });

  useEffect(() => {
    return () => {
      if (statusRef.current === PlayerStatus.INIT) {
        _cancelStream();
      }
    };
  }, [_cancelStream]);

  useEffect(() => {
    if (!streamId.current && game) {
      const init = async () => {
        let keyboardMap: string | undefined = undefined;
        try {
          keyboardMap = await getKeyboardLayoutMap();
        } catch (e) {
          logW(LOGGING_TAG, "Caught error requesting keyboard map", e);
        }
        let launchStreamOpts: Partial<LaunchStreamOpts> | undefined = undefined;
        try {
          launchStreamOpts = await NativeModule.module?.getLaunchStreamOpts?.();
        } catch (e) {
          logW(LOGGING_TAG, "Caught error requesting launchStreamOpts", e);
        }
        launchStream(gameId, {
          gameParameters,
          keyboardLayout: keyboardMap,
          ...launchStreamOpts,
        }).subscribe({
          next: (res) => {
            if (canceled.current) {
              // https://blacknut.atlassian.net/browse/CA-3731
              // race condition when calling back while /start has not yet returned
              cancelStream(res.stream.id).subscribe({
                error: (err: unknown) => {
                  logE(LOGGING_TAG, "Caught error on cancel", err);
                },
              });
              onCancelLaunch?.();
              return;
            }

            if (res.userConf) {
              setStreamUserConf(res.userConf);
            }
            streamId.current = res.stream.id;
            datadogLogs.setGlobalContextProperty("stream", res.stream.id);
            cancelPolling.current = pollStream(res.stream.id, {
              onCompleted: (final, meta) => {
                setStream(final);
                setStreamMeta(meta);
              },
              onError: (e) => onError(e),
              onProgress: (progress, meta) => {
                setProgress(progress);
                setStreamMeta(meta);
              },
              onCanceled: () => {
                logD(LOGGING_TAG, "Canceled");
              },
            });
          },
          error: (err: ApiError) => {
            onError({
              code: err.code || WebRTCErrorCode.LAUNCH_ERROR,
              message: err.toString() || "Unknown error",
            });
          },
        });
      };
      init();
    }
  }, [game, gameId, gameParameters, setStream, onError]);

  // Listen for stop events
  // On init state, cancel stream
  useEffect(() => {
    if (playerStatus === PlayerStatus.INIT) {
      eventEmitter.on(EVENT_EMITTER_EVENT_STOP, _cancelStream);
      return () => {
        eventEmitter.off(EVENT_EMITTER_EVENT_STOP, _cancelStream);
      };
    }
  }, [_cancelStream, playerStatus]);

  return {
    gameLoading,
    progress,
    tips,
    streamMeta,
    // progressProps,
  };
};

export default useGameLaunching;
